import { ChangeDetectionStrategy, Component, NgZone, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { SectionFinishDialogComponent } from '../../../section-finish-dialog/section-finish-dialog.component';
import { GuidedTourWrapperComponent } from '../../guided-tour-wrapper.component';
import { GuidedTourHighlightDialogComponent } from '../../highlight-dialog/guided-tour-highlight-dialog.component';
import { GroupAssignmentDemoComponent } from '../my-groups/group-assignment-demo.component';
import { ModalBridgeComponent } from '@markant-platform/shared/ui-modal';
import { GuidedTourDirective } from '../../guided-tour.directive';
import { GuidedTourStep } from '../../../model/guided-tour-step';
import { NavigationEnd, Router, Scroll } from '@angular/router';
import { GuidedTourService } from '../../../service/guided-tour.service';
import { OA_ADMIN_AREA } from '../../../model/guided-tour';
import { OrganizationMemberDetailDemoComponent } from './organization-member-detail-demo/organization-member-detail-demo.component';
import { BehaviorSubject, filter, firstValueFrom, of } from 'rxjs';
import { HighlightDialogConfig } from '../../../model/highlight-dialog';
import { MyEmployeeSteps, stepDialogConfigMap } from './my-employees-steps';
import { ManagementViewDemoComponent } from '../my-services/management-view-demo/management-view-demo.component';

@Component({
  selector: 'markant-platform-my-employees',
  standalone: true,
  imports: [
    CommonModule,
    SectionFinishDialogComponent,
    GuidedTourWrapperComponent,
    GuidedTourHighlightDialogComponent,
    GroupAssignmentDemoComponent,
    ModalBridgeComponent,
    OrganizationMemberDetailDemoComponent,
    ManagementViewDemoComponent,
  ],
  templateUrl: './my-employees.component.html',
  styleUrl: './my-employees.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MyEmployeesComponent extends GuidedTourDirective {
  @ViewChild(GuidedTourWrapperComponent) guidedTourComponent!: GuidedTourWrapperComponent;

  enableClickThrough = true;
  navigationUserId: string | undefined = undefined;
  doubleBackSteps = [3, 6, 7];

  steps: GuidedTourStep[] = [
    ...MyEmployeeSteps,
    {
      highlightTargetId: 'invite-user-button',
      description: {
        text: 'guidedTour.tours.guided-tour-oa-admin-area.section3.description.step13',
        buttons: [
          {
            id: 'tour-finish-button-my-employees',
            text: 'guidedTour.tours.guided-tour-oa-admin-area.section3.step13.endTourButton',
            colorScheme: 'outlined',
            callback: this.showEndDialog.bind(this),
          },
          {
            id: 'tour-add-employee-button',
            text: 'guidedTour.tours.guided-tour-oa-admin-area.section3.step13.addEmployeeButton',
            icon: {
              position: 'trailing',
              id: 'm_arrow-right-small',
            },
            callback: this.openAddEmployee.bind(this),
          },
        ],
      },
    },
  ];

  showHighlightDialog$ = new BehaviorSubject<HighlightDialogConfig | null>(null);

  backButtonCallback = this.previousTarget.bind(this);
  nextButtonCallback = this.nextTarget.bind(this);

  private stepDelay?: number;
  private resizeObserver?: ResizeObserver;

  constructor(
    guidedTourService: GuidedTourService,
    private ngZone: NgZone,
    protected override router: Router
  ) {
    super(OA_ADMIN_AREA, 3, guidedTourService, router);
  }

  override async nextTarget(): Promise<void> {
    super.nextTarget();
    this.enableClickThrough = true;
    this.updateHighlightDialogConfig();

    switch (this.currentStep) {
      case 1:
        await this.navigateTo('/organization');
        break;
      case 2:
        this.stepDelay = window.setTimeout(async () => await this.nextTarget(), 3000);
        break;
      case 3:
        this.cleanTimeoutAndFocus(this.stepDelay);
        break;
      case 5:
        this.waitForElement('action-button-0', async () => {
          this.moveScrollbarLeftOrRight();
          await this.guidedTourComponent.next();
        });
        break;
      case 6:
        this.resizeObserver?.disconnect();
        await firstValueFrom(
          this.router.events.pipe(filter(event => event instanceof NavigationEnd || event instanceof Scroll))
        );
        this.navigationUserId = this.router.url.split('/').pop();
        this.stepDelay = window.setTimeout(async () => await this.nextTarget(), 3000);
        break;
      case 7:
        this.cleanTimeoutAndFocus(this.stepDelay);
        break;
      case 13:
        await this.navigateTo('/organization/members');
        this.enableClickThrough = false;
        setTimeout(async () => await this.guidedTourComponent.next(true), 100);
        break;
    }

    if (this.currentStep === 1) {
      await this.guidedTourComponent.start();
    } else if (this.currentStep <= this.steps.length && this.currentStep !== 5 && this.currentStep !== 13) {
      await this.guidedTourComponent.next();
    }
  }

  protected override async previousTarget(): Promise<void> {
    const isDoubleBackTarget = this.doubleBackSteps.includes(this.currentStep);
    this.updateHighlightDialogConfig(true);

    await this.jumpToTarget(this.currentStep - (isDoubleBackTarget ? 2 : 1));
    if (isDoubleBackTarget) {
      this.currentStep = this.currentStep - 1;
      await this.nextTarget();
    }
  }

  private updateHighlightDialogConfig(backward = false): void {
    const config = stepDialogConfigMap.get(this.currentStep - (backward ? 1 : 0));
    this.showHighlightDialog$.next(config ?? null);
  }

  protected override async jumpToTarget(target: number): Promise<void> {
    super.jumpToTarget(target);
    switch (target) {
      case 0:
        await this.router.navigate(['']);
        this.cancelSection();
        await this.guidedTourComponent.end();
        break;
      case 1:
        this.enableClickThrough = true;
        await this.navigateTo('/organization');
        await this.guidedTourComponent.jumpTo(target - 1);
        this.cleanTimeoutAndFocus(this.stepDelay);
        break;
      case 3:
        this.enableClickThrough = true;
        this.waitForElement('checkbox-header-table', async () => {
          this.moveScrollbarLeftOrRight(true);
          await this.guidedTourComponent.jumpTo(target - 1);
        });
        break;
      case 4:
        this.resizeObserver?.disconnect();
        this.enableClickThrough = true;
        await this.guidedTourComponent.jumpTo(target - 1);
        break;
      case 5:
        await this.navigateTo('/organization/members');
        this.waitForElement('action-button-0', async () => {
          this.moveScrollbarLeftOrRight();
          await this.guidedTourComponent.jumpTo(target - 2);
        });
        break;
      case 12:
        await this.navigateTo(`/organization/members/${this.navigationUserId}`);
        this.enableClickThrough = false;
        await this.guidedTourComponent.jumpTo(target - 1);
        break;
      default:
        this.enableClickThrough = true;
        await this.guidedTourComponent.jumpTo(target - 1);
    }
  }

  private async navigateTo(path: string): Promise<void> {
    await this.ngZone.run(() => this.router.navigate([path]));
    await firstValueFrom(of(true));
  }

  async openAddEmployee(): Promise<void> {
    await this.guidedTourComponent.end();
    (<HTMLDivElement>document.querySelector('#invite-user-button')).dispatchEvent(new MouseEvent('click'));
    await this.completeSection(false);
  }

  async showEndDialog(): Promise<void> {
    await this.guidedTourService.completeSection(this.guidedTour.id, this.sectionNumber, true);
    await this.guidedTourComponent.end();
  }

  private moveScrollbarLeftOrRight(toLeft = false): void {
    const element = document.querySelector('.mdc-data-table__table-container');
    if (element) {
      const htmlElement = element as HTMLElement;
      this.scrollTo(htmlElement, toLeft);

      // Listen for changes in the table and call scrollTo again if something has changed.
      const mutationObserver = new MutationObserver(() => this.scrollTo(htmlElement, toLeft));
      // Call scrollTo again when spotlight is on the right side and user changes the window size.
      this.resizeObserver = new ResizeObserver(() => this.scrollTo(htmlElement, toLeft));

      const tableRow = htmlElement.getElementsByClassName('table-row').item(1);
      if (tableRow) {
        mutationObserver.observe(tableRow, { attributes: true, subtree: true });
        if (!toLeft) {
          this.resizeObserver.observe(document.body);
        }
        setTimeout(() => mutationObserver.disconnect(), 5000);
      }
    }
  }

  private scrollTo(htmlElement: HTMLElement, toLeft: boolean): void {
    htmlElement.scrollTo(toLeft ? 0 : 5000, 0);
  }

  private waitForElement(elementId: string, callback: () => void): void {
    const element = document.getElementById(elementId);
    if (element) {
      return callback();
    }
    setTimeout(() => this.waitForElement(elementId, callback), 100);
  }
}
